<template>
  <div :class="[calendarClass, 'vdp-datepicker__calendar']" :style="calendarStyle">
    <header>
      <span>{{ isYmd ? currYearName : currMonthName }}
        {{ isYmd ? currMonthName : currYearName }}</span>
    </header>
    <div :class="isRtl ? 'flex-rtl' : ''">
      <span class="cal-cell day-header" v-for="d in daysOfWeek" :key="d.timestamp">{{ d }}
      </span>
      <template v-if="blankDays > 0">
        <span class="cal-cell day blank" v-for="d in blankDays" :key="d.timestamp">
          <div class="day-number-wrapper">
            <span class="day-number">&nbsp;</span>
          </div>
          <div v-for="coderNumber in getCoderSlots()" :key="coderNumber" class="day-slot">
            &nbsp;
          </div>
        </span>
      </template>
      <template v-for="day in days">
        <span class="cal-cell day" :key="day.timestamp" :class="dayClasses(day)">
          <div class="day-number-wrapper">
            <span class="day-number">{{day.date}}</span>
          </div>
          <template v-for="coderNumber in getCoderSlots()">
            <div v-if="coderExists(day, coderNumber)" :key="coderNumber" :style="getCoderStyle(day, coderNumber)"
              class="day-slot">
              <template v-if="isCodersFirstDay(day, coderNumber)">
                {{isCodersFirstDay(day, coderNumber) ? `Coder ${coderNumber}` : '&nbsp;' }}
              </template>
              <template v-else>
                &nbsp;
              </template>
            </div>
            <template v-else>
              <div :key="coderNumber" class="day-slot">
                &nbsp;
              </div>
            </template>
          </template>
        </span>
      </template>
      <small style="display: block; line-height: 0px; padding: 15px 0px 0px 0px;"
        v-if="days.some(x => x.isDisabled)">Days
        within 14 days of the
        current date cannot
        be used</small>
    </div>
  </div>
</template>

<script>
  import { makeDateUtils } from '@/utilities/DateUtils'
  import en from './locale/translations/en'

  export default {
    props: {
      value: Array,
      coders: Array,
      disabled: Boolean,
      showDayView: Boolean,
      pageDate: {
        type: Date,
        default: () => new Date()
      },
      pageTimestamp: Number,
      fullMonthName: Boolean,
      dayCellContent: {
        type: Function,
        default: day => day.date
      },
      disabledDates: Object,
      highlighted: Object,
      calendarClass: [String, Object, Array],
      calendarStyle: Object,
      translation: {
        type: Object,
        default: () => en
      },
      isRtl: Boolean,
      mondayFirst: Boolean,
      useUtc: Boolean
    },
    data() {
      const constructedDateUtils = makeDateUtils(this.useUtc)
      return {
        utils: constructedDateUtils,
        dragging: false
      }
    },
    created: function () {
    },
    computed: {
      masterDates() {
        let masterDates = []
        for (let i = 0; i < this.coders.length; i++) {
          for (let j = 0; j < this.coders[i].scheduledDays.length; j++) {
            masterDates.push({
              coderNumber: i + 1,
              day: this.coders[i].scheduledDays[j].date
            })
          }
        }
        return masterDates
      },
      /**
       * Returns an array of day names
       * @return {String[]}
       */
      daysOfWeek() {
        if (this.mondayFirst) {
          const tempDays = this.translation.days.slice()
          tempDays.push(tempDays.shift())
          return tempDays
        }
        return this.translation.days
      },
      /**
       * Returns the day number of the week less one for the first of the current month
       * Used to show amount of empty cells before the first in the day calendar layout
       * @return {Number}
       */
      blankDays() {
        const d = this.pageDate
        let dObj = this.useUtc
          ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
          : new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
        if (this.mondayFirst) {
          return this.utils.getDay(dObj) > 0 ? this.utils.getDay(dObj) - 1 : 6
        }
        return this.utils.getDay(dObj)
      },
      /**
       * @return {Object[]}
       */
      days() {
        const d = this.pageDate
        let days = []
        // set up a new date object to the beginning of the current 'page'
        let dObj = this.useUtc
          ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
          : new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
        let daysInMonth = this.utils.daysInMonth(this.utils.getFullYear(dObj), this.utils.getMonth(dObj))

        for (let i = 0; i < daysInMonth; i++) {
          days.push({
            date: this.utils.getDate(dObj),
            timestamp: dObj.getTime(),
            isToday: this.utils.compareDates(dObj, new Date()),
            isWeekend: this.utils.getDay(dObj) === 0 || this.utils.getDay(dObj) === 6,
            isSaturday: this.utils.getDay(dObj) === 6,
            isSunday: this.utils.getDay(dObj) === 0
          })
          this.utils.setDate(dObj, this.utils.getDate(dObj) + 1)
        }
        return days
      },
      /**
       * Gets the name of the month the current page is on
       * @return {String}
       */
      currMonthName() {
        const monthName = this.fullMonthName ? this.translation.months : this.translation.monthsAbbr
        return this.utils.getMonthNameAbbr(this.utils.getMonth(this.pageDate), monthName)
      },
      /**
       * Gets the name of the year that current page is on
       * @return {Number}
       */
      currYearName() {
        const yearSuffix = this.translation.yearSuffix
        return `${this.utils.getFullYear(this.pageDate)}${yearSuffix}`
      },
      /**
       * Is this translation using year/month/day format?
       * @return {Boolean}
       */
      isYmd() {
        return this.translation.ymd && this.translation.ymd === true
      }
    },
    methods: {
      getCoderSlots() {
        let uniqueCoders = [...new Set(this.masterDates.map(x => x.coderNumber))]
        uniqueCoders.push(0) // add an empty slot for a blank line in each day
        return uniqueCoders
      },
      coderExists(day, coderNumber) {
        return this.masterDates.some(x => x.day === day.date && x.coderNumber === coderNumber)
      },
      isCodersFirstDay(day, coderNumber) {
        let coderDates = this.masterDates.filter(x => x.coderNumber === coderNumber)
        coderDates.sort((a, b) => a.day - b.day)
        return !coderDates.some(x => x.day === day.date - 1)
      },
      isCodersLastDay(day, coderNumber) {
        let coderDates = this.masterDates.filter(x => x.coderNumber === coderNumber)
        coderDates.sort((a, b) => a.day - b.day)
        return !coderDates.some(x => x.day === day.date + 1)
      },
      getCoderStyle(day, coderNumber) {
        let index = coderNumber - 1
        let colors = ['rgba(0, 71, 118, .5)', 'rgba(88, 89, 91, .5)', 'rgba(255, 196, 12, .5)', 'rgba(136, 185, 91, .5)', '#A4B2C5', '#767984', '#B0DBE5']
        let style = `background: ${colors[index % 7]};`

        if (this.isCodersFirstDay(day, coderNumber)) {
          style += `border-top-left-radius: 10px; border-bottom-left-radius: 10px;`
        }
        if (this.isCodersLastDay(day, coderNumber)) {
          style += 'border-top-right-radius: 10px; border-bottom-right-radius: 10px;'
        }
        return style
      },
      dayClasses(day) {
        return {
          'weekend': day.isWeekend && !day.isSelected,
          'sat': day.isSaturday,
          'sun': day.isSunday
        }
      }
    }
  }
</script>

<style lang="stylus">
  @import './styles'
</style>

<style scoped>
  .vdp-datepicker__calendar {
    width: 100% !important;
  }

  .cal-cell {
    height: 100% !important;
    line-height: inherit;
    width: 14.08%;
    vertical-align: top;
  }

  .day {
    text-align: left;
    border: none;
    border-bottom: 1px solid #ccc;
  }

  .day:not(.sun):not(.blank) {
    border-left: 1px solid #ccc;
  }

  .day-slot {
    text-align: center;
    font-size: 13px;
    width: 100%;
    height: 15px;
    line-height: 15px;
    padding: 2px 0px 2px 0px;
    margin-bottom: 1px;
    color: #2c3e50;
  }

  /*skips the first instance of .blank*/
  .blank~.blank {
    border-left: 1px solid transparent;
  }

  .sun>.day-slot,
  .blank>.day-slot {
    border-left: none;
  }

  .sun>.day-number-wrapper,
  .blank>.day-number-wrapper {
    border-left: none;
  }

  .day-header {
    background: aliceblue;
    margin: 10px 0px 0px 0px;
    padding-bottom: 5px;
    padding-top: 5px;
    border-bottom: 1px solid #ccc;
    border-left: 1px solid transparent;
    border-right: none;
    border-top: none;
  }

  .day-header:hover {
    background: aliceblue !important;
  }

  .day-header:first-child {
    border-left: none;
  }

  .day:last-child {
    border-right: 1px solid #ccc;
  }

  .day:hover {
    border-left: 1px solid #ccc !important;
  }

  .day:last-child:hover {
    border-right: 1px solid #ccc !important;
  }

  .sun:hover,
  .blank:hover {
    border-left: none !important;
  }

  .vdp-datepicker__calendar .cal-cell:not(.blank):not(.disabled).day:hover,
  .vdp-datepicker__calendar .cal-cell:not(.blank):not(.disabled).month:hover,
  .vdp-datepicker__calendar .cal-cell:not(.blank):not(.disabled).year:hover {
    border-bottom: 1px solid #ccc;
    border-top: none;
    border-right: none;
    border-left: none;
    cursor: default;
  }

  .day-number {
    margin: 2px 0px 0px 3px;
  }
</style>
